import $ from "jquery";
import React from "react";
import ResponsiveCitationView from "../../components/citation/ResponsiveCitationView";
import styles from "./citations.module.scss";

/**
 * Processes DITA citations in content, converting xref cite elements to clickable citations
 * and implementing CSS-only title citations with invisible clickable overlays
 */
export class DitaCitationProcessor {
  /**
   * @param {Function|null} setReferencePopup
   */
  constructor(setReferencePopup = null) {
    this.processedContent = false;
    this.setReferencePopup = setReferencePopup;
  }

  process(contentSelector = "#contentId") {
    if (this.processedContent) {
      return;
    }

    const $content = $(contentSelector);
    if ($content.length === 0) {
      return;
    }

    try {
      // Process xref cite elements and replace with numbered citations
      this.processXrefCiteElements($content);

      // Process title citations using CSS-only approach
      this.processTitleCitationsCSS($content);

      // Ensure references list is numbered
      this.numberReferencesList($content);

      this.processedContent = true;
    } catch (error) {
      console.error("Error processing DITA citations:", error);
    }
  }

  processXrefCiteElements($content) {
    // Find all xref elements that contain cite elements
    const $xrefCites = $content.find("xref").has("cite");

    $xrefCites.each((index, xref) => {
      const $xref = $(xref);
      const href = $xref.attr("href") || "";
      const $cite = $xref.find("cite");
      const citationText = $cite.text().trim();

      let referenceNumber = null;

      // Extract id by getting value after the last slash
      const lastSlashIndex = href.lastIndexOf("/");
      if (lastSlashIndex !== -1) {
        const liId = href.substring(lastSlashIndex + 1);
        referenceNumber = this.getReferenceIndex($content, liId);
      }

      // If unable to find number from index, fall back to old way
      if (!referenceNumber) {
        let referenceMatch = href.match(/li__ENREF_\d+_(\d+)/);
        if (!referenceMatch) {
          referenceMatch = href.match(/li__ENREF_(\d+)/);
        }
        if (referenceMatch) {
          referenceNumber = parseInt(referenceMatch[1]);
        }
      }

      if (referenceNumber) {
        const citationLink = this.createCitationLink(
          referenceNumber,
          citationText,
          href,
        );
        $xref.replaceWith(citationLink);
      } else {
        // Keep original citation text if we can't extract number
        $xref.replaceWith(
          `<span class="${styles["citation-text"]}">${citationText}</span>`,
        );
      }
    });
  }

  processTitleCitationsCSS($content) {
    const $sections = $content.find("section[id*='topic'], section.showtandfsection");

    $sections.each((index, section) => {
      const $section = $(section);
      const $h2Title = $section.children("div").children("h2.topicontainer, h3.dartsectiondisplay").first();
      const $ditaContent = $section.children("ditacontent").length > 0
        ? $section.children("ditacontent")
        : $section.children("div.colapsecontent, section.showtandfsection").children("ditacontent");
      const $firstParagraph = $ditaContent.find("conbody > p").first();

      if (!this.isValidTitleCitationCandidate($h2Title, $firstParagraph)) {
        return;
      }

      const $citationLinks = $firstParagraph.find(
        "span[class*='citation-link']",
      );

      if (
        $citationLinks.length > 0 &&
        this.isCitationOnlyParagraph($firstParagraph, $citationLinks)
      ) {
        this.setupTitleCitations(
          $h2Title,
          $firstParagraph,
          $citationLinks,
          $section.attr("id"),
        );
      }
    });
  }

  /**
   * Check if elements are valid candidates for title citation processing
   */
  isValidTitleCitationCandidate($h2Title, $firstParagraph) {
    return $h2Title.length > 0 && $firstParagraph.length > 0;
  }

  /**
   * Check if paragraph contains only citations (no other meaningful text)
   */
  isCitationOnlyParagraph($paragraph, $citationLinks) {
    const $tempParagraph = $paragraph.clone();
    $tempParagraph.find("span[class*='citation-link']").remove();
    return $tempParagraph.text().trim() === "";
  }

  setupTitleCitations($h2Title, $firstParagraph, $citationLinks, sectionId) {
    // Extract citation numbers and data
    const { citationNumbers, citationData } =
      this.extractCitationData($citationLinks);

    if (citationNumbers.length > 0) {
      // Wrap title text in a span to control citation placement
      this.wrapTitleTextWithCitations($h2Title, citationNumbers);

      // Create clickable overlay for interaction
      this.createClickableCitationOverlay($h2Title, citationData);

      // Hide original citation paragraph
      this.hideCitationParagraph($firstParagraph);
    }
  }

  /**
   * Wrap the title text (not buttons) in a span with citation pseudo-element
   */
  wrapTitleTextWithCitations($h2Title, citationNumbers) {
    // Get all child nodes of the H2
    const childNodes = $h2Title.contents();

    // Find text nodes and wrap them in a span
    let titleTextNodes = [];
    childNodes.each((i, node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        titleTextNodes.push(node);
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        !$(node).is("button, span.citation-overlay")
      ) {
        // Include non-button elements in title text
        titleTextNodes.push(node);
      }
    });

    if (titleTextNodes.length > 0) {
      // Create wrapper span for title text with citations
      // Set up CSS pseudo-element display
      const citationText = citationNumbers.map((num) => `[${num}]`).join(" ");
      const $titleWrapper = $(
        `<span class="${styles["title-with-citations"]}" data-title-citations="${citationText}"></span>`,
      );

      // Move title text nodes into wrapper
      titleTextNodes.forEach((node) => {
        $titleWrapper.append($(node));
      });

      // Insert wrapper at beginning of H2
      $h2Title.prepend($titleWrapper);
    }
  }

  extractCitationData($citationLinks) {
    const citationNumbers = [];
    const citationData = [];

    $citationLinks.each((i, citationLink) => {
      const $link = $(citationLink);
      const citationNumber =
        $link.attr("data-citation-number") ||
        $link.text().match(/\[(\d+)\]/)?.[1];

      if (citationNumber) {
        citationNumbers.push(citationNumber);
        citationData.push({
          number: $link.attr("data-citation-number"),
          text: $link.attr("data-citation-text"),
          href: $link.attr("data-citation-href"),
        });
      }
    });

    return { citationNumbers, citationData };
  }

  hideCitationParagraph($paragraph) {
    $paragraph.css({
      display: "none",
      height: "0",
      margin: "0",
      padding: "0",
      visibility: "hidden",
    });
  }

  createMultipleCitationOverlays($h2Title, citationData, citationText) {
    const $titleWrapper = $h2Title.find("span[data-title-citations]");
    if ($titleWrapper.length === 0) {
      console.log("Title wrapper not found for multiple overlays");
      return;
    }

    // Parse citation text to get individual citations and their positions
    const citations = citationText.match(/\[\d+\]/g) || [];

    citations.forEach((citationMatch, index) => {
      const citationNumber = citationMatch.match(/\[(\d+)\]/)[1];

      // Find matching citation data
      const citationInfo =
        citationData.find((c) => c.number === citationNumber) ||
        citationData[0];

      // Create individual overlay for this citation
      const $overlay = $(
        `<span class="${styles["individual-citation-overlay"]}" data-citation-index="${index}" data-citation-number="${citationNumber}"></span>`,
      );

      $overlay.on("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.showCitationPopup(
          parseInt(citationInfo.number, 10),
          citationInfo.text,
          citationInfo.href,
        );
      });

      $titleWrapper.append($overlay);
    });
  }

  createClickableCitationOverlay($h2Title, citationData) {
    const $citationOverlay = $(
      `<span class="${styles["citation-overlay"]}"></span>`,
    );

    $citationOverlay.on("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      // For title citations, show all citations together
      this.showTitleCitationsPopup(citationData);
    });

    // Attach overlay to the title wrapper span, not the entire H2
    const $titleWrapper = $h2Title.find("span[data-title-citations]");

    if ($titleWrapper.length > 0) {
      $titleWrapper.append($citationOverlay);
    } else {
      // Fallback: attach to H2 if wrapper not found
      $h2Title.append($citationOverlay);
    }
  }

  createCitationLink(citationNumber, citationText, originalHref) {
    const $link = $(`
            <span class="${styles["citation-link"]}"
                  data-citation-number="${citationNumber}"
                  data-citation-text="${citationText}"
                  data-citation-href="${originalHref}">
                [${citationNumber}]
            </span>
        `);

    $link.on("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      this.showCitationPopup(citationNumber, citationText, originalHref);
    });

    return $link;
  }

  numberReferencesList($content) {
    // Find references sections by looking for headers containing "references"
    const $referencesHeaders = $content
      .find("h1, h2, h3, h4")
      .filter(function () {
        return $(this).text().toLowerCase().includes("references");
      });

    $referencesHeaders.each((index, header) => {
      const $header = $(header);

      // Find the list that follows this header
      let $list = $header.nextAll("ul, ol").first();
      if ($list.length === 0) {
        // Try looking in the parent's next siblings
        $list = $header.parent().nextAll().find("ul, ol").first();
      }

      if ($list.length > 0) {
        // Convert to ordered list if it's unordered
        if ($list.is("ul")) {
          const $orderedList = $(
            `<ol class="${styles["references-list"]}"></ol>`,
          );
          $list.find("li").each(function () {
            $orderedList.append($(this).clone());
          });
          $list.replaceWith($orderedList);
        } else {
          // Just add the class if it's already ordered
          $list.addClass(styles["references-list"]);
        }
      }
    });
  }

  getReferenceIndex($content, id) {
    const $targetLi = $content.find(`li#${id}`);
    if ($targetLi.length === 0) {
      return null;
    }

    const $parentList = $targetLi.parent();
    if (!$parentList.is("ol, ul")) {
      return null;
    }

    const $allListItems = $parentList.children("li");
    const index = $allListItems.index($targetLi);
    return index !== -1 ? index + 1 : null;
  }

  showTitleCitationsPopup(citationData) {
    if (!this.setReferencePopup) {
      console.warn(
        "Citation popup unavailable: setReferencePopup not provided",
      );
      return;
    }

    // Get full reference text for all citations
    const allCitations = citationData
      .map((citation) => {
        const fullReferenceText = this.getFullReferenceText(
          parseInt(citation.number, 10),
          citation.href,
        );
        return {
          number: parseInt(citation.number, 10),
          text: citation.text,
          fullReference: fullReferenceText, // Use 'fullReference' to match hook expectations
          fullReferenceText, // Keep for backward compatibility
        };
      })
      .filter((citation) => citation.fullReferenceText); // Only include citations with found references

    if (allCitations.length === 0) {
      // Fallback to first citation if no full references found
      const firstCitation = citationData[0];
      this.showCitationPopup(
        parseInt(firstCitation.number, 10),
        firstCitation.text,
        firstCitation.href,
      );
      return;
    }

    const handleClose = () => {
      this.setReferencePopup("");
    };

    const handleViewAllReferences = () => {
      this.scrollToReferencesSection();
      this.setReferencePopup("");
    };

    if (allCitations.length === 1) {
      // Single citation - display normally
      const citation = allCitations[0];
      const modalElement = React.createElement(ResponsiveCitationView, {
        citationNumber: citation.number,
        citationText: citation.text,
        fullReferenceText: citation.fullReferenceText,
        onClose: handleClose,
        onViewAllReferences: handleViewAllReferences,
      });
      this.setReferencePopup(modalElement);
      return;
    }

    // Multiple citations - pass structured data for better formatting
    const firstCitationNumber = allCitations[0].number;
    const citationNumbers = allCitations.map((c) => c.number).join(", ");

    const modalElement = React.createElement(ResponsiveCitationView, {
      citationNumber: firstCitationNumber, // This will show as superscript in modal
      citationText: `Multiple references (${citationNumbers})`,
      multipleCitations: allCitations, // Pass structured citation array
      onClose: handleClose,
      onViewAllReferences: handleViewAllReferences,
    });

    this.setReferencePopup(modalElement);
  }

  /**
   * Show popup for single citation (paragraph citations)
   */
  showCitationPopup(citationNumber, citationText, originalHref) {
    if (!this.setReferencePopup) {
      console.warn(
        "Citation popup unavailable: setReferencePopup not provided",
      );
      return;
    }

    const fullReferenceText = this.getFullReferenceText(
      citationNumber,
      originalHref,
    );

    const handleClose = () => {
      this.setReferencePopup("");
    };

    const handleViewAllReferences = () => {
      this.scrollToReferencesSection();
      this.setReferencePopup("");
    };

    const modalElement = React.createElement(ResponsiveCitationView, {
      citationNumber,
      citationText,
      fullReferenceText,
      onClose: handleClose,
      onViewAllReferences: handleViewAllReferences,
    });

    this.setReferencePopup(modalElement);
  }

  /**
   * Helper method to scroll to references section
   */
  scrollToReferencesSection() {
    let referencesElement = document.getElementById("references");
    if (!referencesElement) {
      // Try finding by heading text
      const headings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
      for (let heading of headings) {
        if (heading.textContent.toLowerCase().includes("references")) {
          referencesElement = heading;
          break;
        }
      }
    }

    if (referencesElement) {
      referencesElement.scrollIntoView({ behavior: "smooth" });
    } else {
      console.log("References section not found");
    }
  }

  /**
   * Find full reference text using multiple lookup strategies
   * @param {number} citationNumber - Citation number to find
   * @param {string} originalHref - Original DITA href for ID-based lookup
   * @returns {string|null} Full reference text or null if not found
   */
  getFullReferenceText(citationNumber, originalHref) {
    // ID-based lookup from DITA href (most reliable)
    const referenceById = this.findReferenceById(originalHref);
    if (referenceById) return referenceById;

    // Position-based lookup in references list
    return this.findReferenceByPosition(citationNumber);
  }

  findReferenceById(originalHref) {
    if (!originalHref) return null;

    const idMatch = originalHref.match(/(li__ENREF_\d+_\d+)/);
    if (idMatch) {
      const referenceId = idMatch[1];
      const $referenceById = $(`#${referenceId}`);
      if ($referenceById.length > 0) {
        return $referenceById.text().trim();
      }
    }
    return null;
  }

  findReferenceByPosition(citationNumber) {
    // Try styled references list first
    const $referencesList = $(`.${styles["references-list"]}`);
    if ($referencesList.length > 0) {
      const $referenceItem = $referencesList.find(
        `li:nth-child(${citationNumber})`,
      );
      if ($referenceItem.length > 0) {
        return $referenceItem.text().trim();
      }
    }

    // Try references section with ordered lists
    const $referencesSection = $("h1, h2, h3, h4").filter(function () {
      return $(this).text().toLowerCase().includes("references");
    });

    if ($referencesSection.length > 0) {
      const $orderedList =
        $referencesSection.nextAll("ol").first() ||
        $referencesSection.parent().find("ol").first();

      if ($orderedList.length > 0) {
        const $referenceItem = $orderedList.find(
          `li:nth-child(${citationNumber})`,
        );
        if ($referenceItem.length > 0) {
          return $referenceItem.text().trim();
        }
      }
    }

    // Try DITA ID pattern matching
    const $referenceById = $(`[id*="ENREF"][id*="_${citationNumber}"]`);
    if ($referenceById.length > 0) {
      return $referenceById.text().trim();
    }

    // Last resort: position-based lookup in any ordered list
    const $allListItems = $("ol li");
    const $matchingItem = $allListItems.eq(citationNumber - 1);
    if ($matchingItem.length > 0) {
      return $matchingItem.text().trim();
    }

    return null;
  }
}

export function processDitaCitationsInDOM(dartSelectors) {
  return new Promise((resolve, reject) => {
    try {
      const setReferencePopup = dartSelectors?.setReferencePopup || null;
      const processor = new DitaCitationProcessor(setReferencePopup);
      processor.process();
      resolve();
    } catch (error) {
      console.error("Error processing DITA citations:", error);
      reject(error);
    }
  });
}

export default DitaCitationProcessor;